<template>
  <div class="competition-athletes">

    <!-- 🔷 PRIMER BLOQUE: Encabezado con botones -->
    <div class="athletes-header-top">
      <div class="title-row">
        <h3>{{ $t("athletes.title") }}</h3>
        <div class="register-actions" v-if="!isOwnerOrganizer(user, competition)">
          <button
              v-if="showRegisterButton && competition.is_open && competition.publish_at && !competition.started_at"
              class="register-btn"
              @click="handleRegister"
          >
            <i class="fa-solid fa-ticket-alt"></i>{{ $t('competitions.join') || 'Join / Participar' }}
          </button>
        </div>
      </div>
      <div class="buttons-group">
        <button class="toggle-view-btn" @click="toggleViewMode">
          <i :class="viewMode === 'grid' ? 'fa fa-list' : 'fa fa-th-large'"></i>
        </button>
        <button v-if="isOwnerOrganizer(user,competition) && !competition.started_at" class="invite-btn" @click="openInviteModal">
          <i class="fa-solid fa-user-plus"></i> {{ $t("athletes.invite") }}
        </button>
        <button v-if="needDeclinedAthletes" class="declined-btn" @click="toggleDeclinedAthletes">
          <i class="fa-solid fa-user-slash"></i>
          {{ showDeclined ? $t("athletes.hide_declined") : $t("athletes.show_declined") }}
        </button>
        <button v-if="needSendMailAthletes" class="send-mail-btn" @click="confirmSendEmailToAthletes">
          <i class="fa-solid fa-mail-bulk"></i>
          {{ $t("athletes.send_mails_athtletes") }}
        </button>
      </div>
    </div>

    <AthleteSummaryBar
      v-if="isOwnerOrganizer(user,competition) && !competition.started_at"
      :competition="competition"
      :compact="false"
      :activeFilter="activeFilter"
      @filter="handleSummaryFilter"
    />

    <!-- 🔹 VISTA DE ATLETAS -->
    <template v-if="filteredAthletes.length > 0">
      <!-- Grid -->
      <div v-if="viewMode === 'grid'" :class="['athlete-grid', paginatedAthletes.length <= 2 ? 'single' : '']">
        <AthleteCard
            v-for="athlete in paginatedAthletes"
            :key="athlete.id"
            :athlete="athlete"
            :competition="competition"
            @viewAthlete="handleViewAthlete"
            @update-show-modal="controlPop"
            @send-email-athlete="handleSendEmail"

        />
      </div>
      <!-- Tabla -->
      <AthleteTable
          v-if="viewMode === 'list'"
          :athletes="filteredAthletes"
          :competition="competition"
          @viewAthlete="handleViewAthlete"
          @update-show-modal="controlPop"
      />
      <div class="pagination-controls" v-if="viewMode === 'grid' && totalPages > 1">
        <button class="pagination-btn" @click="currentPage--" :disabled="currentPage === 1">
          {{ $t('athletes.pagination.prev') }}
        </button>

        <span class="pagination-text">
          {{ $t('athletes.pagination.page') }} {{ currentPage }} {{ $t('athletes.pagination.of') }} {{ totalPages }}
        </span>

        <button class="pagination-btn" @click="currentPage++" :disabled="currentPage === totalPages">
          {{ $t('athletes.pagination.next') }}
        </button>
      </div>
    </template>

    <p v-else class="no-athletes">{{ $t("athletes.empty_athletes") }}</p>

    <!-- Lista de rechazados -->
    <template v-if="showDeclined && declinedAthletes.length > 0">
      <h4>{{ $t("athletes.declined_list") }}</h4>
      <AthleteTable
          :athletes="declinedAthletes"
          :competition="competition"
      />
    </template>

    <!-- Modal -->
    <InviteAthleteModal
        :isVisible="isInviteModalVisible"
        :athletes="athletes_list"
        :competition="competition"
        @closeModal="closeInviteModal"
        @inviteAthletes="handleInviteAthletes"
    />
    <!-- Modal -->
    <OrderAthletesModal
        :isVisible="isOrderModalVisible"
        :athletes="filteredAthletes"
        @closeModal="closeOrderModal"
        @orderAthletes="handleOrderAthletes"

    />
    <ConfirmationModal
        ref="confirmationModal"
        :message="modalMessage"
        :showCancel="showCancel"
        @confirmed="confirmModal"
        @canceled="cancelModal"
    />
  </div>
</template>

<script>
import AthleteCard from "@/components/competitions/AthleteCard.vue";
import InviteAthleteModal from "@/components/competitions/InviteAthleteModal.vue";
import OrderAthletesModal from "@/components/competitions/OrderAthletesModal.vue";
import AthleteTable from "@/components/competitions/AthleteTable.vue";
import AthleteSummaryBar from "@/components/competitions/AthleteSummaryBar.vue";
import { isOwnerOrganizer} from '@/helpers/roleUtils';
import {mapState} from "vuex";
import ConfirmationModal from "@/components/competitions/ConfirmationModal.vue";
export default {
  name: "CompetitionAthletes",
  components: {OrderAthletesModal, ConfirmationModal, AthleteCard, InviteAthleteModal, AthleteTable, AthleteSummaryBar },
  props: {
    athletes: {
      type: Array,
      required: true,
      default: () => [],
    },
    athletes_list: {
      type: Array,
      required: true,
      default: () => [],
    },
    competition: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      isInviteModalVisible: false,
      isOrderModalVisible: false,
      viewMode: 'grid',
      showSendMail: false,
      showDeclined: false,
      showCancel: false,
      modalMessage:"",
      pendingAction: null,
      pendingId:null,
      currentPage: 1,
      itemsPerPage: 9,
      activeFilter: null,
    };
  },
  computed: {
    ...mapState("competition",["competitionIdToView", "competitionData"]),
    ...mapState('competition_users', ['competitionUsers']),
    ...mapState('user', ['isAuthenticated','user','token']),
    isOrganizerManager() {
      const hasOrganizerRole = this.user?.roles?.some(r => r.name === 'Organizer' || r.id === 4);
      const hasSupervisorRole = this.user?.roles?.some(r => r.name === 'Supervisor');
      return hasOrganizerRole || hasSupervisorRole || this.isOwnerOrganizer(this.user, this.competition);
    },
    canViewApprovedOnly() {
      return this.user && Array.isArray(this.user.roles) && this.user.roles.some(r => ['Athlete', 'Judge'].includes(r.name));
    },
    filteredAthletes() {
      const filtered = this.athletes.filter(athlete => {
        const isAthlete = athlete.role_id === 1;
        if (!isAthlete) return false;

        const isDeclined = athlete.status_id === 5;
        const filteringDeclined = this.activeFilter === 'declined';
        if (!filteringDeclined && isDeclined) return false;

        // Si es organizador, puede ver todos y aplicar filtros manuales
        if (this.isOrganizerManager) return this.matchesActiveFilter(athlete);

        // Si es el mismo atleta (usuario actual)
        const isSelf = athlete?.user_id === this.user?.id;
        const sendedMail = athlete.status_id === 2 || athlete.status_id === 3;
        if (isSelf && sendedMail) return this.matchesActiveFilter(athlete);

        // Si solo puede ver aprobados (judge o athlete), verifica el status
        if (this.canViewApprovedOnly) {
          const allowedStatusIds = [4, 18];
          const allowed = allowedStatusIds.includes(athlete.status_id);
          return allowed && this.matchesActiveFilter(athlete);
        }

        return false;
      });

      // Ordenar por order_number si está definido
      return filtered.sort((a, b) => {
        const aOrder = a.order_number ?? Infinity;
        const bOrder = b.order_number ?? Infinity;
        return aOrder - bOrder;
      });
    },
    declinedAthletes() {
      return this.athletes.filter(athlete =>
          athlete.role_id === 1 &&
          athlete.status.id === 5 &&
          this.isOwnerOrganizer(this.user,this.competition)
      );
    },
    needSendMailAthletes() {
      return this.athletes.some(athlete =>
          athlete.role_id === 1 &&
          athlete.status_id === 1 &&
          this.isOwnerOrganizer(this.user, this.competition)
      );
    },
    needDeclinedAthletes() {
      return this.athletes.some(athlete =>
          athlete.role_id === 1 &&
          athlete.status_id === 5 &&
          this.isOwnerOrganizer(this.user, this.competition)
      );
    },
    isReadyForOrder() {
      return this.athletes.some(athlete =>
          athlete.role_id === 1 &&
          athlete.status_id === 4 &&
          this.isOwnerOrganizer(this.user, this.competition)
      );
    },

    paginatedAthletes() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredAthletes.slice(start, start + this.itemsPerPage);
    },

    totalPages() {
      return Math.ceil(this.filteredAthletes.length / this.itemsPerPage);
    }

  ,
    showRegisterButton() {
      const user = this.user;
      if (!user || !user.id || !Array.isArray(user.roles)) return false;
      const roles = user.roles;
      const userIds = (this.competitionUsers || []).map(u => u.user.id);
      const alreadyUser = userIds.includes(user.id);
      const hasAthleteRole = roles.some(role => role.name === 'Athlete');
      return !alreadyUser && hasAthleteRole;
    },
    showRegisteredButton() {
      const user = this.user;
      if (!user || !user.id) return false;
      const userIds = (this.competitionUsers || []).map(u => u.user.id);
      return userIds.includes(user.id);
    }

  },
  watch: {
    filteredAthletes(newList) {
      const maxPage = Math.max(1, Math.ceil((newList?.length || 0) / this.itemsPerPage));
      if (this.currentPage > maxPage) {
        this.currentPage = maxPage;
      }
    }
  },
  methods: {
    isOwnerOrganizer,
    handleSummaryFilter(filter) {
      this.activeFilter = filter;
      this.currentPage = 1;
    },
    matchesActiveFilter(athlete) {
      if (!this.activeFilter) return true;
      switch (this.activeFilter) {
        case 'approved':
          return athlete.status_id === 4;
        case 'declined':
          return athlete.status_id === 5;
        case 'pending':
          // Solo mostrar pendientes en status 3 (aceptar/confirmar por atleta)
          return athlete.status_id === 3;
        case 'invited':
          return athlete.status_id === 2;
        case 'paid':
          return Boolean(athlete.fees_paid);
        default:
          return true;
      }
    },
    openInviteModal() {
      this.isInviteModalVisible = true;
    },
    openOrderModal() {
      this.isOrderModalVisible = true;
    },
    closeInviteModal() {
      this.isInviteModalVisible = false;
    },
    closeOrderModal() {
      this.isOrderModalVisible = false;
    },
    openOrderModalExternal() {
      this.isOrderModalVisible = true;
    },
    handleInviteAthletes(selectedAthletes,email) {
      this.$emit("inviteAthletes", selectedAthletes,email);
    },
    handleSendEmail(athleteId) {
      this.$emit('sendEmailToAthlete', athleteId);
    },
    handleViewAthlete(athleteId) {
      this.$emit("viewAthlete", athleteId);
    },
    handleOrderAthletes(athletes) {
      this.closeOrderModal();
      this.$emit("orderAthletes", athletes);
    },
    toggleViewMode() {
      this.viewMode = this.viewMode === 'grid' ? 'list' : 'grid';
    },
    toggleDeclinedAthletes() {
      this.showDeclined = !this.showDeclined;
    },
    sendEmailToAthletes() {
      if (!this.isOwnerOrganizer(this.user, this.competition)) return;
      this.$emit('sendEmailToAthletes', this.competition.id);
    },
    handleRegister() {
      this.$emit('register', this.competition.id);
    },
    controlPop(action,id,message){
      this.modalMessage = message;
      this.showCancel = true;
      this.pendingAction = action;
      this.pendingId = id;
      this.$refs.confirmationModal.openModal(); // 🔥 Usa `ref` para abrir el modal
    },
    confirmSendEmailToAthletes() {
      if (!this.isOwnerOrganizer(this.user, this.competition)) return;
      this.controlPop(
          'sendEmailAthletes',
          this.competition.id,
          this.$t('athletes.modal.send_all_invitation_confirmation')
      );
    },
    async confirmModal() {
      switch (this.pendingAction) {
        case 'deleteAthlete':
          this.$emit('deleteAthlete', this.pendingId);
          break;
        case 'rejectAthlete':
          this.$emit('rejectAthlete', this.pendingId);
          break;
        case 'confirmAthlete':
          this.$emit('confirmAthlete', this.pendingId);
          break;
        case 'approveAthlete':
          this.$emit('approveAthlete', this.pendingId);
          break;
        case 'confirmPayment':
          this.$emit('confirmPayment', this.pendingId);
          break;
        case 'sendEmailAthlete':
          this.$emit('sendEmailToAthlete', this.pendingId);
          break;
        case 'sendEmailAthletes':
          this.$emit('sendEmailToAthletes', this.pendingId);
          break;
          // Agregá más acciones según necesites
        default:
          console.warn("Acción no reconocida:", this.pendingAction);
      }

      // Limpiar los temporales
      this.pendingAction = null;
      this.pendingId = null;
    },
    cancelModal() {
      this.pendingAction = null;
      this.pendingId = null;
      this.selectedCompetitionId = null;
    },

  }
};
</script>


<style scoped>
.competition-athletes {
  margin-top: 20px;
}

.header-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.buttons-group {
  display: flex;
  gap: 10px;
}

.toggle-view-btn {
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 18px;
}

.invite-btn {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
}

.invite-btn:hover {
  background-color: #0056b3;
}

.declined-btn {
  background-color: rgba(91, 91, 91, 0.99);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
}
.order-btn {
  background-color: rgba(152, 115, 227, 0.4);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
}

.send-mail-btn {
  background-color: rgba(134, 112, 65, 0.99);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
}

.declined-btn:hover {
  background-color: rgba(91, 91, 91, 0.99);
}
.send-mail-btn:hover {
  background-color: rgba(91, 91, 91, 0.99);
}

.athlete-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}
.athlete-grid.single {
  justify-content: center;
}

.athlete-grid.single .athlete-card {
  max-width: 250px;
  width: 100%;
}
.athlete-grid .athlete-card {
  max-width: 200px;
  width: 100%;
}

.no-athletes {
  text-align: center;
  font-size: 14px;
  color: #777;
}

.athletes-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.register-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.register-btn, .registered-btn {
  background-color: #ffc107;
  color: #000;
  border: none;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.registered-btn {
  background-color: #28a745;
  color: white;
}
.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.pagination-btn {
  background-color: #4c56af;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #3b4491;
}

.pagination-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.pagination-text {
  font-size: 14px;
  color: #444;
}

@media (max-width: 576px) {
  .athlete-grid {
    grid-template-columns: repeat(auto-fit, minmax(170px, 2fr));
    gap: 2px;
  }
  .athlete-grid .athlete-card {
    max-width: 170px;
  }

  .athlete-grid.single .judge-card {
    max-width: 100%;
  }
}

@media (max-width: 768px) {

}
</style>